/**
 * Core / Mint / Methods: Core
 * ------------------------------------------------------------------------------
 * - Export of core methods for the renderless app.
 * - Mostly handles Pinia actions.
 *
 */
import CorePiniaActions from '~/core/mint/methods/core-pinia-actions'

import { debounce } from '~/helpers/utils'
import { stringToHandle } from '~/helpers/strings'
import { waitFor } from '~/helpers/dom'

/**
 * State object for Cart page reloading.
 */
const state = {
  reloadCart: false,
}

/**
 * Export methods.
 * @return {Object}
 */
export default () => {
  return {

    /**
     * Pinia actions.
     */
    ...CorePiniaActions(),

    /**
     * Set Event Listeners.
     */
    setEventListeners() {

      /**
       * The window has been resized.
       */
      visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), 500),
      )
    },

    /**
     * Set Event Bus Listeners.
     */
    setEventBusListeners() {

      /**
       * A feature flag has been toggled.
       * - Event is emitted from mint-core and this event
       * ensures that Section components are kept in sync.
       */
      mint.EventBus.on('mint:toggle:flag', (flags) => {
        if (!Array.isArray(flags)) {
          return
        }

        flags.forEach((flag) => {
          this.toggleFeatureFlag(flag)
        })
      })

      /**
       * The Global-E locale has been set.
       * - Event is emitted from mint-core and this event
       * ensures that Section components are kept in sync.
       */
      mint.EventBus.on('mint:locale:set', (response) => {
        this.setLocale(response, true)
        this.handleCartMarketSwitch(response)
      })

      /**
       * The cart data has been updated.
       * - Event is emitted from Pinia/Cart
       * - This event ensures each different Vue instance are up-to-date.
       */
      mint.EventBus.on('mint:cart:updated', (response) => {
        this.handleCartPageEmpty(response)

        /**
         * This should run *after* reload instructions, else reload will fail.
         */
        if (state.reloadCart) {
          return
        }

        this.setCart(response)
        this.setGeolocationAttributes()
      })

      /**
       * The cart data has been updated.
       * - Event is emitted from Pinia/Cart
       * - This event ensures each different Vue instance are up-to-date.
       */
      mint.EventBus.on('mint:wishlist:updated', (response) => {
        this.setWishlist(response)
      })

      /**
       * The product object has been updated.
       * - Used on the PDP templates only.
       * - This event ensures each different Vue instance are up-to-date.
       */
      if (mint.request.pageType === 'product') {
        mint.EventBus.on('mint:product:updated', (response) => {
          const instanceId = response.instanceId
          this.setProduct({
            completeTheLook: response.completeTheLook,
            product: response.productData,
            variant: response.currentVariant,
            instanceId,
          })
        })
      }
    },

    /**
     * Set geolocation attribute in the cart.
     * - This is so that emails have access to this data and can
     *   conditionally render templates accordingly.
     */
    setGeolocationAttributes() {
      const hasGeolocationAttribute = Boolean(
        this.cartData.attributes._mint_geolocation,
      )

      const {
        continentCode,
        country,
      } = mint.shop

      /**
       * Check if the cart currently has a geolocation attribute
       * - Check if it matches the users current geolocation
       * - If there is a match, do nothing
       * - If it doesn't match, then update the cart
       */
      if (hasGeolocationAttribute) {
        const storedCountryCode = this.cartData.attributes._mint_geolocation.split(':')[1]

        const countryCodeMatches = Boolean(
          stringToHandle(storedCountryCode) === stringToHandle(country),
        )

        if (countryCodeMatches) {
          return
        }
      }

      if (typeof continentCode === 'string' && country === 'string') {
        return
      }

      /**
       * Set the attribute in the cart.
       */
      this.updateAttributes({
        attributes: {
          _mint_geolocation: `${continentCode}:${country}`,
        },
      })
    },

    /**
     * Handle Market-switching on the Cart page.
     * Cart page does not support dynamic data, so force a page reload.
     * - Used if the customer switches markets.
     * @param {Object} payload - Response from `mint:locale:set`
     */
    handleCartPageEmpty(payload) {
      if (
        payload.request.event === 'cart:cleared' &&
        mint.request.pageType === 'cart'
      ) {
        window.location.reload()
      }
    },

    /**
     * Handle events relating to the cart upon customer switching markets.
     * @param {Object} payload - Response from `mint:locale:set`
     */
    handleCartMarketSwitch(payload) {
      if (!mint.flags?.international?.emptyBasketMarketSwitch) {
        state.reloadCart = false
        return
      }

      const cartHasProducts = (this.cartData.item_count > Number(0))

      if (!cartHasProducts && !payload.states.hasSwitchedMarkets) {
        return
      }

      state.reloadCart = true

      this.clearCart()
    },

    /**
     * Initialise Wishlist
     * - Wait for global variable to be ready
     * - Set state in Pinia
     * - Pinia will relay to all sections.
     */
    initWishlist() {
      mint.EventBus.on('wishlist:ready', () => {
        this.handleWishlistUpdate()
      })
    },
  }
}
